import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import api from '../services/api';
import Loading from '../components/Common/Loading';
import '../styles/CreateAdminPage.css';

interface Venue {
  id: string;
  name: string;
}

const CreateAdminPage: React.FC = () => {
  const { isSuperAdmin } = useAdminAuth();
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [venue, setVenue] = useState<string>('');
  const [role, setRole] = useState<string>('admin');
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [fetchingVenues, setFetchingVenues] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const history = useHistory();
  
  // Reset venue when role changes to superadmin
  useEffect(() => {
    if (role === 'superadmin') {
      setVenue('');
    }
  }, [role]);
  
  useEffect(() => {
    // Only superadmin can access this page
    if (!isSuperAdmin) {
      history.replace('/admin/dashboard');
    }
    
    fetchVenues();
  }, [isSuperAdmin, history]);
  
  const fetchVenues = async () => {
    try {
      setFetchingVenues(true);
      const response = await api.get('/admin/venues');
      setVenues(response.data);
    } catch (err) {
      console.error('Failed to fetch venues:', err);
      setError('Failed to load venues. Please try again.');
    } finally {
      setFetchingVenues(false);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    
    // Validation
    if (!email || !password || !confirmPassword) {
      setError('Email and password fields are required');
      return;
    }
    
    // Validate venue is selected for regular admin
    if (role !== 'superadmin' && !venue) {
      setError('Please select a venue for this admin');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    try {
      setLoading(true);
      console.log(`Creating user with role: ${role}`);
      
      // Prepare request data - only include venueId for regular admin
      const requestData = {
        email,
        password,
        role,
        ...(role !== 'superadmin' && { venueId: venue })
      };
      
      // Use the API helper function to create admin
      await api.post('/admin/create', requestData);
      
      setSuccess('Admin account created successfully');
      
      // Clear form
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setVenue('');
      setRole('admin');
      
      // Immediately redirect to admin dashboard with admin management tab active
      history.push('/admin/dashboard?tab=admins');
      
    } catch (err: any) {
      console.error('Failed to create admin:', err);
      setError(err.response?.data?.message || 'Failed to create admin account');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="create-admin-page">
      <div className="create-admin-container">
        <div className="create-admin-header">
          <h1>Create Admin</h1>
          <button 
            onClick={() => history.push('/admin/dashboard')}
            className="back-button"
          >
            Back to Dashboard
          </button>
        </div>
        
        {fetchingVenues ? (
          <Loading />
        ) : (
          <form onSubmit={handleSubmit} className="create-admin-form">
            {error && <div className="create-admin-error">{error}</div>}
            {success && <div className="create-admin-success">{success}</div>}
            
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="confirm-password">Confirm Password</label>
              <input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="role">Role</label>
              <select
                id="role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                <option value="admin">Admin</option>
                <option value="superadmin">Super Admin</option>
              </select>
            </div>
            
            {role !== 'superadmin' && (
              <div className="form-group">
                <label htmlFor="venue">Venue</label>
                <select
                  id="venue"
                  value={venue}
                  onChange={(e) => setVenue(e.target.value)}
                  required
                >
                  <option value="">Select a venue</option>
                  {venues.map(v => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {role === 'superadmin' && (
              <div className="info-message-container">
                <div className="info-message">Super Admins have access to all venues</div>
              </div>
            )}
            
            <button 
              type="submit" 
              className="create-button" 
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Admin'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default CreateAdminPage;